import type { RemoteData } from '../async-actions';
import { createFetchAction } from '../async-actions';
import { getSession } from '../helpers/Session';
import * as Hub from '../ogm-hub';
import { getSiteTypeFromId } from './shared';

export type RestrictionListWithDeletePolicy = Hub.RestrictionList & {
	canDelete: boolean;
};

export interface RestrictionListsWithDeletePolicy {
	lists: RestrictionListWithDeletePolicy[];
	totalPages: number;
}

const pageSize = 24;

export interface RestrictionsHomeState {
	loadingRestrictionsLists: boolean;
	remoteListing: RemoteData<RestrictionListsWithDeletePolicy, string[]>;
	showSupplier: boolean;
	showProductCount: boolean;
	siteType: 'dssi' | 'sysco' | null;
	listBeingDeleted:
		| {
				modalOpen: true;
				isDeleting: boolean;
				list: Hub.RestrictionList;
		  }
		| {
				modalOpen: false;
		  };
	pageIndex: number;
}

export function initialState(): RestrictionsHomeState {
	return {
		loadingRestrictionsLists: false,
		remoteListing: { type: 'initial' },
		showSupplier: false,
		showProductCount: false,
		listBeingDeleted: {
			modalOpen: false,
		},
		siteType: null,
		pageIndex: 1,
	};
}

export function RestrictionReducer(
	state: RestrictionsHomeState = initialState(),
	action: ListingAction
): RestrictionsHomeState {
	switch (action.type) {
		case '@restr-list/RESTRICTION_LISTING_PAGE_SUCCESS': {
			const lists = action.payload.lists.map((x) => {
				const canDelete =
					state.siteType === 'sysco' ? x.locationCount === 0 : true;
				return {
					...x,
					canDelete,
				};
			});

			let totalPages = 1;
			if (action.payload.totalCount >= 0) {
				totalPages = Math.ceil(action.payload.totalCount / pageSize);
			}

			return {
				...state,
				loadingRestrictionsLists: false,
				remoteListing: {
					type: 'success',
					data: { lists, totalPages },
				},
			};
		}
		case '@restr-list/RESTRICTION_LISTING_PAGE_PENDING': {
			const { siteTypeId } = action.payload;
			const siteType = getSiteTypeFromId(siteTypeId);
			const showSupplier = siteType === 'dssi';
			const showProductCount = siteType === 'dssi' || siteType === 'sysco';
			const pageIndex = state.pageIndex;
			return {
				...initialState(),
				loadingRestrictionsLists: true,
				remoteListing: { type: 'loading' },
				showSupplier,
				showProductCount,
				siteType,
				pageIndex,
			};
		}
		case '@restr-list/RESTRICTION_LISTING_PAGE_FAILURE': {
			return {
				...state,
				loadingRestrictionsLists: false,
				remoteListing: { type: 'failure', err: action.errors },
			};
		}

		case '@restr-list/DELETE_CLICKED': {
			const id = action.payload;
			const list =
				state.remoteListing.type === 'success'
					? state.remoteListing.data.lists.find((x) => x.id === id)
					: null;

			return {
				...state,
				listBeingDeleted: {
					modalOpen: true,
					isDeleting: false,
					list,
				},
			};
		}

		case '@restr-list/DELETE_CANCELED': {
			return {
				...state,
				listBeingDeleted: { modalOpen: false },
			};
		}

		case '@restr-list/DELETE_CONFIRMED_PENDING': {
			if (state.listBeingDeleted.modalOpen) {
				return {
					...state,
					listBeingDeleted: {
						...state.listBeingDeleted,
						isDeleting: true,
					},
				};
			}

			return state;
		}

		case '@restr-list/DELETE_CONFIRMED_FAILURE': {
			if (state.listBeingDeleted.modalOpen) {
				return {
					...state,
					listBeingDeleted: {
						...state.listBeingDeleted,
						isDeleting: false,
					},
				};
			}

			return state;
		}

		case '@restr-list/DELETE_CONFIRMED_SUCCESS': {
			if (state.listBeingDeleted.modalOpen) {
				const lists = action.payload.lists.map((x) => {
					const canDelete =
						state.siteType === 'sysco' ? x.locationCount === 0 : true;
					return {
						...x,
						canDelete,
					};
				});

				let totalPages = 1;
				if (action.payload.totalCount >= 0) {
					totalPages = Math.ceil(action.payload.totalCount / pageSize);
				}

				return {
					...state,
					listBeingDeleted: { modalOpen: false },
					remoteListing: {
						type: 'success',
						data: { lists, totalPages },
					},
				};
			}

			return state;
		}

		case '@restr-list/PAGE_CHANGED': {
			return {
				...state,
				pageIndex: action.payload,
			};
		}

		default: {
			return state;
		}
	}
}

export type ListingAction =
	| RESTRICTION_LISTING_PAGE_PENDING
	| RESTRICTION_LISTING_PAGE_FAILURE
	| RESTRICTION_LISTING_PAGE_SUCCESS
	| DELETE_CLICKED
	| DELETE_CANCELED
	| DELETE_CONFIRMED_PENDING
	| DELETE_CONFIRMED_FAILURE
	| DELETE_CONFIRMED_SUCCESS
	| PAGE_CHANGED;

export interface RESTRICTION_LISTING_PAGE_PENDING {
	type: '@restr-list/RESTRICTION_LISTING_PAGE_PENDING';
	payload: { siteTypeId: number };
}

export interface RESTRICTION_LISTING_PAGE_FAILURE {
	type: '@restr-list/RESTRICTION_LISTING_PAGE_FAILURE';
	errors: string[];
}

export interface RESTRICTION_LISTING_PAGE_SUCCESS {
	type: '@restr-list/RESTRICTION_LISTING_PAGE_SUCCESS';
	payload: Hub.GetRestrictionListingResult;
}

export const restrictionHomePageLoaded = createFetchAction<
	{},
	RESTRICTION_LISTING_PAGE_PENDING,
	RESTRICTION_LISTING_PAGE_FAILURE,
	RESTRICTION_LISTING_PAGE_SUCCESS
>(
	'@restr-list/RESTRICTION_LISTING_PAGE_PENDING',
	'@restr-list/RESTRICTION_LISTING_PAGE_FAILURE',
	'@restr-list/RESTRICTION_LISTING_PAGE_SUCCESS',
	(args, dispatch, getState) => {
		const pageIndex = getState().restrictionsHome.pageIndex;
		return Hub.RestrictionsController.GetRestrictionListing(
			pageSize,
			pageIndex
		);
	},
	() => {
		const { siteTypeId } = getSession();
		return {
			siteTypeId,
		};
	}
);

// TODO generalize modal action creation?

export interface DELETE_CLICKED {
	type: '@restr-list/DELETE_CLICKED';
	payload: number;
}

export function deleteClicked(listId: number): DELETE_CLICKED {
	return {
		type: '@restr-list/DELETE_CLICKED',
		payload: listId,
	};
}

export interface DELETE_CANCELED {
	type: '@restr-list/DELETE_CANCELED';
	payload: {};
}

export function deleteCanceled(): DELETE_CANCELED {
	return {
		type: '@restr-list/DELETE_CANCELED',
		payload: {},
	};
}

export interface DELETE_CONFIRMED_PENDING {
	type: '@restr-list/DELETE_CONFIRMED_PENDING';
}

export interface DELETE_CONFIRMED_FAILURE {
	type: '@restr-list/DELETE_CONFIRMED_FAILURE';
	errors: string[];
}

export interface DELETE_CONFIRMED_SUCCESS {
	type: '@restr-list/DELETE_CONFIRMED_SUCCESS';
	payload: Hub.DeleteRestrictionListResult;
}

export const deleteRestrictionListConfirmed = createFetchAction<
	{},
	DELETE_CONFIRMED_PENDING,
	DELETE_CONFIRMED_FAILURE,
	DELETE_CONFIRMED_SUCCESS
>(
	'@restr-list/DELETE_CONFIRMED_PENDING',
	'@restr-list/DELETE_CONFIRMED_FAILURE',
	'@restr-list/DELETE_CONFIRMED_SUCCESS',
	(args, dispatch, getState) => {
		const state = getState().restrictionsHome;

		const id = state.listBeingDeleted.modalOpen
			? state.listBeingDeleted.list.id
			: null;

		if (!id) {
			throw new Error('tried to delete a nonexistant list');
		}

		if (state.remoteListing.type === 'success') {
			state.pageIndex =
				state.remoteListing.data.lists.length - 1 === 0
					? state.pageIndex - 1 < 1
						? 1
						: state.pageIndex - 1
					: state.pageIndex;
		}

		return Hub.RestrictionsController.DeleteRestrictionList({
			restrictionListId: id,
			pageSize: pageSize,
			pageIndex: state.pageIndex,
		});
	}
);

export interface PAGE_CHANGED {
	type: '@restr-list/PAGE_CHANGED';
	payload: number;
}

export function changePage(pageNumber: number) {
	return {
		type: '@restr-list/PAGE_CHANGED',
		payload: pageNumber,
	};
}
